<template>
  <div style="width: 308px">
    <Button type="button" severity="info" outlined @click="duplicateWidget" style="margin-right: 10px;">Duplicate</Button>
    <Button type="button" severity="danger" outlined @click="deleteWidget">Delete</Button>
    <ModelDataStylePanel />
  </div>
</template>
<script setup lang="ts">

import { nanoid } from 'nanoid'
import {PPTDragDropElement} from "@/types/slides";
import {storeToRefs} from "pinia";
import {useCustomerStore, useMainStore, useSlidesStore, useWidgetStore} from "@/store";
import {computed} from "vue";
import ModelDataStylePanel from "@/views/Editor/Toolbar/ElementStylePanel/WidgetStylePanel/ModelDataStylePanel.vue"

const widgetStore = useWidgetStore()
const {  selectedDragDropId, selectedWidget } = storeToRefs(widgetStore)
const { updated} = storeToRefs(useMainStore())

const slidesStore = useSlidesStore()
const customerStore = useCustomerStore()


const element = computed(() => {
    return selectedWidget.value
}) as any | null

const deleteWidget = () => {
  const dragDropElement: PPTDragDropElement = <PPTDragDropElement>slidesStore.getElementsByType('dragdrop').find((zone) => zone.id === selectedDragDropId.value)
  const reactiveJson = dragDropElement ? dragDropElement.widgets : []
  const elementId = element.value?.id

  const parentIndex = reactiveJson.findIndex(parent =>
      parent.children.some(child => child.id === elementId)
  );

  if (parentIndex !== -1) {
    const childIndex = reactiveJson[parentIndex].children.findIndex(child =>
        child.id === elementId
    )
    if (childIndex !== -1) {
      const child = reactiveJson[parentIndex].children[childIndex];

      if (child.subType === 'customer_image_button') {
        customerStore.removeImage(child.id)
      }

      reactiveJson[parentIndex].children.splice(childIndex, 1);
      updated.value = Date.now()
    }
  }

  widgetStore.setActiveWidgets([])
  updated.value = Date.now()
}

const duplicateWidget = () => {
  const dragDropElement: PPTDragDropElement = <PPTDragDropElement>slidesStore.getElementsByType('dragdrop').find((zone) => zone.id === selectedDragDropId.value)
  const reactiveJson = dragDropElement ? dragDropElement.widgets : []
  const elementId = element.value?.id

  const parentIndex = reactiveJson.findIndex(parent =>
      parent.children.some(child => child.id === elementId)
  )

  if (parentIndex !== -1) {
    const childIndex = reactiveJson[parentIndex].children.findIndex(child =>
        child.id === elementId
    )
    if (childIndex !== -1) {
      const originalWidget = reactiveJson[parentIndex].children[childIndex]
      const clonedWidget = JSON.parse(JSON.stringify({ ...originalWidget, id: nanoid(10) }))

      reactiveJson[parentIndex].children.splice(childIndex + 1, 0, clonedWidget)
      widgetStore.setActiveWidgets([clonedWidget])
    }
  }
  updated.value = Date.now()
}

</script>
<style scoped>
:deep(.p-tabview-nav-container) {
  position: relative;
}

:deep(.p-tabview-scrollable .p-tabview-nav-container) {
  overflow: hidden;
}

:deep(.p-tabview-nav-content) {
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
  overscroll-behavior: contain auto;
}

:deep(.p-tabview-nav) {
  display: flex;
  margin: 0;
  padding: 0;
  list-style-type: none;
  flex: 1 1 auto;
}

:deep(.p-tabview-header-action) {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  position: relative;
  text-decoration: none;
  overflow: hidden;
}

:deep(.p-tabview-ink-bar) {
  display: none;
  z-index: 1;
}

:deep(.p-tabview-header-action:focus) {
  z-index: 1;
}

:deep(.p-tabview-title) {
  line-height: 1;
  white-space: nowrap;
}

:deep(.p-tabview-nav-btn) {
  position: absolute;
  top: 0;
  z-index: 2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.p-tabview-nav-prev) {
  left: 0;
}

:deep(.p-tabview-nav-next) {
  right: 0;
}

:deep(.p-tabview-nav-content::-webkit-scrollbar) {
  display: none;
}

:deep(.p-tabview .p-tabview-nav) {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-width: 0 0 1px 0;
}
:deep(.p-tabview .p-tabview-nav li) {
  margin-right: 0;
}
:deep(.p-tabview .p-tabview-nav li .p-tabview-nav-link) {
  border: solid #e2e8f0;
  border-width: 0 0 1px 0;
  border-color: transparent transparent #e2e8f0 transparent;
  background: #ffffff;
  color: #64748b;
  padding: 1rem 1.125rem;
  font-weight: 600;
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s, outline-color 0.2s;
  margin: 0 0 -1px 0;
  outline-color: transparent;
}
:deep(.p-tabview .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus-visible) {
  outline: 1px solid var(--p-focus-ring-color);
  outline-offset: -2px;
  box-shadow: inset none;
}
:deep(.p-tabview .p-tabview-nav li:not(.p-highlight):not(.p-disabled):hover .p-tabview-nav-link) {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #334155;
}
:deep(.p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #6366F1;
}
:deep(.p-tabview .p-tabview-nav-btn.p-link) {
  background: #ffffff;
  color: #6366F1;
  width: 2.5rem;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
  border-radius: 0;
  outline-color: transparent;
}
:deep(.p-tabview .p-tabview-nav-btn.p-link:focus-visible) {
  outline: 1px solid var(--p-focus-ring-color);
  outline-offset: -2px;
  box-shadow: inset none;
}
:deep(.p-tabview .p-tabview-panels) {
  background: #ffffff;
  padding: 0.875rem 1.125rem 1.125rem 1.125rem;
  border: 0 none;
  color: #334155;
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
}

:deep(.p-accordion .p-accordion-header .p-accordion-header-link) {
  padding: 1rem;
  border: 1px solid #dee2e6;
  color: #495057;
  background: #f8f9fa;
  font-weight: 600;
  border-radius: 3px;
  transition: box-shadow 0.2s;
  outline-color: transparent;
}
:deep(.p-accordion .p-accordion-header .p-accordion-header-link .p-accordion-toggle-icon) {
  margin-right: 0.5rem;
}

:deep(.p-accordion-header-action) {
  text-decoration: none;
}

</style>
